import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Placeholder } from './Placeholder';
import { clampBoxToImage, isValidBox } from './crop-geometry';

interface Props {
  src?: string;
  box?: number[];
  alt: string;
  placeholder: string;
}

export function CroppedImage({ src, box, alt, placeholder }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [hasError, setHasError] = useState(false);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setContainerSize({ width: rect.width, height: rect.height });
    const observer = new ResizeObserver((entries) => {
      const contentRect = entries[0].contentRect;
      setContainerSize({
        width: contentRect.width,
        height: contentRect.height,
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      setNaturalSize({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    },
    []
  );

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  if (!src || !isValidBox(box) || hasError) {
    return <Placeholder text={placeholder} />;
  }

  const crop = naturalSize ? clampBoxToImage(box, naturalSize) : null;

  if (naturalSize && !crop) {
    return <Placeholder text={placeholder} />;
  }

  const ready =
    crop &&
    containerSize &&
    containerSize.width > 0 &&
    containerSize.height > 0;
  const scale = ready
    ? Math.min(
        containerSize.width / crop.width,
        containerSize.height / crop.height
      )
    : 0;

  return (
    <div
      ref={containerRef}
      className="bg-onlight-bg-2 dark:bg-ondark-bg-2 relative flex h-full w-full items-center justify-center overflow-hidden"
    >
      {!naturalSize && (
        <>
          <img
            src={src}
            alt={alt}
            className="invisible absolute"
            onLoad={handleLoad}
            onError={handleError}
          />
          <span className="text-onlight-text-2 dark:text-ondark-text-2 text-xs">
            Loading...
          </span>
        </>
      )}
      {ready && naturalSize && (
        <div
          className="relative overflow-hidden"
          style={{ width: crop.width * scale, height: crop.height * scale }}
        >
          <img
            src={src}
            alt={alt}
            onError={handleError}
            style={{
              position: 'absolute',
              width: naturalSize.width * scale,
              height: naturalSize.height * scale,
              left: -crop.x * scale,
              top: -crop.y * scale,
              maxWidth: 'none',
            }}
          />
        </div>
      )}
    </div>
  );
}

export default CroppedImage;
